import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

/**
 * Service de gestion des images pour les véhicules
 * Utilise Firebase Storage pour stocker les images
 */

/**
 * Upload une image de véhicule vers Firebase Storage
 * @param {File} file - Fichier image à uploader
 * @param {string} vehicleId - ID du véhicule
 * @param {string} imageType - Type d'image (main, interior, exterior, etc.)
 * @returns {Promise<string>} URL de téléchargement de l'image
 */
export const uploadVehicleImage = async (file, vehicleId, imageType = 'main') => {
  try {
    // Valider le fichier
    if (!file) {
      throw new Error('Aucun fichier fourni');
    }

    // Valider le type de fichier
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Type de fichier non supporté. Utilisez JPG, PNG ou WebP.');
    }

    // Valider la taille (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('La taille du fichier ne doit pas dépasser 5MB');
    }

    // Créer un nom de fichier unique
    const timestamp = Date.now();
    const extension = file.name.split('.').pop();
    const fileName = `${imageType}_${timestamp}.${extension}`;
    
    // Créer la référence du fichier dans Storage
    const storageRef = ref(storage, `vehicles/${vehicleId}/${fileName}`);
    
    // Upload le fichier
    const snapshot = await uploadBytes(storageRef, file);
    
    // Obtenir l'URL de téléchargement
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading vehicle image:', error);
    throw new Error(`Erreur lors de l'upload de l'image: ${error.message}`);
  }
};

/**
 * Upload plusieurs images de véhicule
 * @param {FileList|Array<File>} files - Liste de fichiers à uploader
 * @param {string} vehicleId - ID du véhicule
 * @returns {Promise<Array<string>>} Liste des URLs des images uploadées
 */
export const uploadMultipleVehicleImages = async (files, vehicleId) => {
  try {
    const uploadPromises = Array.from(files).map((file, index) => {
      const imageType = index === 0 ? 'main' : `image_${index}`;
      return uploadVehicleImage(file, vehicleId, imageType);
    });
    
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw new Error('Erreur lors de l\'upload des images');
  }
};

/**
 * Supprimer une image de véhicule
 * @param {string} imageUrl - URL de l'image à supprimer
 * @returns {Promise<void>}
 */
export const deleteVehicleImage = async (imageUrl) => {
  try {
    if (!imageUrl) {
      throw new Error('URL d\'image non fournie');
    }

    // Extraire le chemin du fichier depuis l'URL
    const url = new URL(imageUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    
    // Créer la référence du fichier
    const storageRef = ref(storage, path);
    
    // Supprimer le fichier
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting vehicle image:', error);
    throw new Error('Erreur lors de la suppression de l\'image');
  }
};

/**
 * Supprimer toutes les images d'un véhicule
 * @param {string} vehicleId - ID du véhicule
 * @returns {Promise<void>}
 */
export const deleteAllVehicleImages = async (vehicleId) => {
  try {
    // Créer la référence du dossier
    const folderRef = ref(storage, `vehicles/${vehicleId}`);
    
    // Lister tous les fichiers
    const listResult = await listAll(folderRef);
    
    // Supprimer tous les fichiers
    const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error deleting all vehicle images:', error);
    throw new Error('Erreur lors de la suppression des images');
  }
};

/**
 * Obtenir toutes les images d'un véhicule
 * @param {string} vehicleId - ID du véhicule
 * @returns {Promise<Array<string>>} Liste des URLs des images
 */
export const getVehicleImages = async (vehicleId) => {
  try {
    // Créer la référence du dossier
    const folderRef = ref(storage, `vehicles/${vehicleId}`);
    
    // Lister tous les fichiers
    const listResult = await listAll(folderRef);
    
    // Obtenir les URLs de téléchargement
    const urlPromises = listResult.items.map(itemRef => getDownloadURL(itemRef));
    const urls = await Promise.all(urlPromises);
    
    return urls;
  } catch (error) {
    console.error('Error getting vehicle images:', error);
    return [];
  }
};

/**
 * Compresser une image avant l'upload
 * @param {File} file - Fichier image à compresser
 * @param {number} maxWidth - Largeur maximale
 * @param {number} maxHeight - Hauteur maximale
 * @param {number} quality - Qualité de compression (0-1)
 * @returns {Promise<Blob>} Image compressée
 */
export const compressImage = async (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculer les nouvelles dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = height * (maxWidth / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = width * (maxHeight / height);
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Erreur lors de la compression'));
            }
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
    reader.readAsDataURL(file);
  });
};

/**
 * Upload une image compressée
 * @param {File} file - Fichier image à uploader
 * @param {string} vehicleId - ID du véhicule
 * @param {string} imageType - Type d'image
 * @returns {Promise<string>} URL de téléchargement
 */
export const uploadCompressedVehicleImage = async (file, vehicleId, imageType = 'main') => {
  try {
    // Compresser l'image
    const compressedBlob = await compressImage(file);
    
    // Créer un nouveau File à partir du Blob
    const compressedFile = new File([compressedBlob], file.name, {
      type: file.type,
      lastModified: Date.now()
    });
    
    // Upload l'image compressée
    return await uploadVehicleImage(compressedFile, vehicleId, imageType);
  } catch (error) {
    console.error('Error uploading compressed image:', error);
    throw new Error('Erreur lors de l\'upload de l\'image compressée');
  }
};

export default {
  uploadVehicleImage,
  uploadMultipleVehicleImages,
  deleteVehicleImage,
  deleteAllVehicleImages,
  getVehicleImages,
  compressImage,
  uploadCompressedVehicleImage
};
